import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo } from 'react';

import { Card } from '../../components/Card';
import { BarCompare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { Pill, type PillTone } from '../../components/Pill';
import { DataTable } from '../../components/Table';
import { getRoundsReport, listBranches } from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtInt,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { ReportHeader } from './ReportHeader';

type Status = 'open' | 'balanced' | 'short' | 'over';
const payoutMethodOf = (method: 'cash_drawer' | 'manager_transfer'): string =>
  method === 'cash_drawer' ? 'จ่ายจากลิ้นชัก' : 'โอนผ่านผู้จัดการ';
const classify = (
  status: 'open' | 'closed' | undefined,
  counted: number,
  diff: number,
): Status =>
  status === 'open' || (status === undefined && counted === 0)
    ? 'open'
    : diff < 0
      ? 'short'
      : diff > 0
        ? 'over'
        : 'balanced';
const STATUS_META: Record<Status, { th: string; tone: PillTone }> = {
  open: { th: 'ยังไม่นับ', tone: 'neutral' },
  balanced: { th: 'เงินตรง', tone: 'ok' },
  short: { th: 'ขาด', tone: 'danger' },
  over: { th: 'เกิน', tone: 'warn' },
};
const fmtRoundTime = (value: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('th-TH-u-nu-latn', {
    timeZone: 'Asia/Bangkok',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

interface RoundRow {
  id: string;
  cashierName: string;
  openedAt: string | null;
  closedAt: string | null;
  branch: string;
  date: string;
  counted: number;
  expected: number;
  diff: number;
  cashDrawerPayout: number;
  managerTransferPayout: number;
  payoutTotal: number;
  pct: number | null;
  status: Status;
}
interface RoundExportRow {
  cashierName: string;
  openedAt: string;
  closedAt: string;
  branch: string;
  date: string;
  countedBaht: string;
  countedSatang: number | null;
  expectedBaht: string;
  expectedSatang: number;
  diffBaht: string;
  diffSatang: number | null;
  payoutTotalBaht: string;
  payoutTotalSatang: number;
  status: string;
}

const columns: ColumnDef<RoundRow, unknown>[] = [
  {
    id: 'round',
    header: 'แคชเชียร์ / เวลาเปิด–ปิด',
    cell: ({ row }) => (
      <div className="min-w-40">
        <div className="font-bold text-ink-1">{row.original.cashierName}</div>
        <div className="text-xs text-ink-4">
          เปิด {fmtRoundTime(row.original.openedAt)} ·{' '}
          {row.original.closedAt
            ? `ปิด ${fmtRoundTime(row.original.closedAt)}`
            : 'ยังไม่ปิด'}
        </div>
      </div>
    ),
  },
  { accessorKey: 'branch', header: 'สาขา' },
  { accessorKey: 'date', header: 'วันที่' },
  {
    accessorKey: 'counted',
    header: 'นับได้',
    cell: (c) => (
      <span className="num">
        {c.row.original.status === 'open'
          ? '—'
          : fmtAmount(c.getValue<number>())}
      </span>
    ),
  },
  {
    accessorKey: 'expected',
    header: 'คาดหวัง',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'diff',
    header: 'ส่วนต่าง',
    cell: (c) => {
      const r = c.row.original;
      if (r.status === 'open') return <span className="text-ink-4">—</span>;
      const v = c.getValue<number>();
      return (
        <span
          className={`num font-bold ${v < 0 ? 'text-danger' : v > 0 ? 'text-warn' : 'text-ok'}`}
        >
          {v > 0 ? '+' : ''}
          {fmtAmount(v)}
        </span>
      );
    },
  },
  {
    accessorKey: 'payoutTotal',
    header: 'ยอดจ่ายออก',
    cell: (c) => (
      <span
        className={`num ${c.getValue<number>() ? 'font-bold' : 'text-ink-4'}`}
      >
        {c.getValue<number>() > 0 ? fmtBaht(c.getValue<number>()) : '—'}
      </span>
    ),
  },
  {
    id: 'payoutBreakdown',
    header: 'แจงยอดจ่ายออก',
    cell: (c) => {
      const r = c.row.original;
      const detail: string[] = [];
      if (r.cashDrawerPayout)
        detail.push(
          `${payoutMethodOf('cash_drawer')} ${fmtBaht(r.cashDrawerPayout)}`,
        );
      if (r.managerTransferPayout)
        detail.push(
          `${payoutMethodOf('manager_transfer')} ${fmtBaht(r.managerTransferPayout)}`,
        );
      if (!detail.length) return <span className="text-ink-4">—</span>;
      return (
        <span className="num font-bold text-ink-3">{detail.join(' / ')}</span>
      );
    },
  },
  {
    accessorKey: 'pct',
    header: '% ต่าง',
    cell: (c) => {
      const v = c.getValue<number | null>();
      return v == null ? (
        <span className="text-ink-4">—</span>
      ) : (
        <span className="num">
          {v > 0 ? '+' : ''}
          {fmtPct(v * 100)}
        </span>
      );
    },
  },
  {
    accessorKey: 'status',
    header: 'สถานะ',
    cell: (c) => {
      const s = STATUS_META[c.getValue<Status>()];
      return <Pill tone={s.tone}>{s.th}</Pill>;
    },
  },
];
const exportCols: ExportCol<RoundExportRow>[] = [
  { key: 'cashierName', header: 'แคชเชียร์' },
  { key: 'openedAt', header: 'เวลาเปิด' },
  { key: 'closedAt', header: 'เวลาปิด' },
  { key: 'branch', header: 'สาขา' },
  { key: 'date', header: 'วันที่' },
  { key: 'countedBaht', header: 'นับได้' },
  { key: 'countedSatang', header: 'นับได้ (satang)' },
  { key: 'expectedBaht', header: 'คาดหวัง' },
  { key: 'expectedSatang', header: 'คาดหวัง (satang)' },
  { key: 'diffBaht', header: 'ส่วนต่าง' },
  { key: 'diffSatang', header: 'ส่วนต่าง (satang)' },
  { key: 'payoutTotalBaht', header: 'ยอดจ่ายออก' },
  { key: 'payoutTotalSatang', header: 'ยอดจ่ายออก (satang)' },
  { key: 'status', header: 'สถานะ' },
];

export function RoundsReportView() {
  const { scope, ready } = useBranchScope();
  const { data, isLoading } = useQuery({
    queryKey: ['report', 'rounds', scope.branchId, scope.allowedBranchIds],
    queryFn: () => getRoundsReport(scope),
    enabled: ready,
  });
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const shortOf = useMemo(() => {
    const m = new Map(branches.map((b) => [b.id, b.short]));
    return (id: string) => m.get(id) ?? id;
  }, [branches]);

  const rows = useMemo<RoundRow[]>(
    () =>
      (data ?? []).map((r) => {
        const payouts = r.payouts ?? [];
        const cashDrawerPayout = payouts.reduce(
          (s, p) => (p.method === 'cash_drawer' ? s + p.amount : s),
          0,
        );
        const managerTransferPayout = payouts.reduce(
          (s, p) => (p.method === 'manager_transfer' ? s + p.amount : s),
          0,
        );
        const payoutTotal = cashDrawerPayout + managerTransferPayout;
        const status = classify(r.status, r.counted, r.diff);
        return {
          id: r.id,
          cashierName: r.cashierName?.trim() || 'ไม่ทราบชื่อ',
          openedAt: r.openedAt ?? null,
          closedAt: r.closedAt ?? null,
          branch: shortOf(r.branchId),
          date: r.date,
          counted: r.counted,
          expected: r.expected,
          diff: r.diff,
          cashDrawerPayout,
          managerTransferPayout,
          payoutTotal,
          pct: status === 'open' || !r.expected ? null : r.diff / r.expected,
          status,
        };
      }),
    [data, shortOf],
  );
  const exportRows = useMemo<RoundExportRow[]>(
    () =>
      rows.map((r) => ({
        cashierName: r.cashierName,
        openedAt: fmtRoundTime(r.openedAt),
        closedAt: r.closedAt ? fmtRoundTime(r.closedAt) : 'ยังไม่ปิด',
        branch: r.branch,
        date: r.date,
        countedBaht: r.status === 'open' ? '—' : fmtBaht(r.counted),
        countedSatang: r.status === 'open' ? null : r.counted,
        expectedBaht: fmtBaht(r.expected),
        expectedSatang: r.expected,
        diffBaht: r.status === 'open' ? '—' : fmtBaht(r.diff),
        diffSatang: r.status === 'open' ? null : r.diff,
        payoutTotalBaht: r.payoutTotal > 0 ? fmtBaht(r.payoutTotal) : '—',
        payoutTotalSatang: r.payoutTotal,
        status: STATUS_META[r.status].th,
      })),
    [rows],
  );

  const closed = rows.filter((r) => r.status !== 'open');
  const balanced = closed.filter((r) => r.status === 'balanced').length;
  const needsReview = closed.filter((r) => r.status !== 'balanced').length;
  const netDiff = closed.reduce((s, r) => s + r.diff, 0);
  const openCount = rows.length - closed.length;
  const balancedPct = closed.length ? (balanced / closed.length) * 100 : 0;
  const reviewRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const priority = (row: RoundRow) =>
          row.status === 'short'
            ? 0
            : row.status === 'over'
              ? 1
              : row.status === 'open'
                ? 2
                : 3;
        return priority(a) - priority(b) || Math.abs(b.diff) - Math.abs(a.diff);
      }),
    [rows],
  );

  const diffChart = useMemo(
    () =>
      closed.map((r) => ({
        label: `${r.cashierName} ${fmtRoundTime(r.openedAt)}`,
        value: r.diff,
      })),
    [closed],
  );
  const byBranch = useMemo(() => {
    const m = new Map<string, number>();
    closed.forEach((r) => m.set(r.branch, (m.get(r.branch) ?? 0) + r.diff));
    return [...m.entries()].map(([label, value]) => ({ label, value }));
  }, [closed]);

  return (
    <div className="bo-fade">
      <ReportHeader
        title="รอบขาย & เงินสด"
        subtitle="กระทบยอดเงินสดแต่ละรอบ · ส่วนต่าง · รอบที่ต้องตรวจสอบ"
        status={{
          label:
            !ready || isLoading ? 'กำลังประมวลผล' : `${rows.length} รอบขาย`,
          tone:
            !ready || isLoading ? 'neutral' : needsReview > 0 ? 'warn' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="รอบขาย"
            title="รอบขาย & เงินสด"
            pdf
          />
        }
      />
      <section
        className={`mb-6 overflow-hidden rounded-[20px] border ${
          needsReview > 0
            ? 'border-warn/35 bg-warn-light/25'
            : 'border-ok/30 bg-ok-light/20'
        }`}
        aria-labelledby="round-review-heading"
      >
        <div className="grid gap-5 px-4 py-5 sm:px-6 lg:grid-cols-[minmax(260px,0.7fr)_minmax(0,1.3fr)] lg:items-center">
          <div>
            <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-ink-3">
              สิ่งที่ต้องจัดการก่อน
            </div>
            <h2
              id="round-review-heading"
              className="mt-2 text-h2 font-bold text-ink"
            >
              {needsReview > 0
                ? `${fmtInt(needsReview)} รอบมีเงินขาดหรือเกิน`
                : 'รอบที่ปิดทั้งหมดเงินตรง'}
            </h2>
            <p className="mt-2 max-w-xl text-body text-ink-2">
              {needsReview > 0
                ? 'ตารางด้านล่างเรียงรอบที่ต้องตรวจสอบขึ้นก่อน ตามด้วยรอบที่ยังเปิดและรอบที่เงินตรง'
                : 'ยังไม่มีส่วนต่างที่ต้องติดตาม ตรวจรอบที่ยังเปิดอยู่ก่อนสิ้นวัน'}
            </p>
          </div>
          <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-line bg-line sm:grid-cols-4">
            {[
              ['ส่วนต่างสุทธิ', `${netDiff > 0 ? '+' : ''}${fmtBaht(netDiff)}`],
              ['รอบยังเปิด', fmtInt(openCount)],
              ['รอบเงินตรง', `${fmtInt(balanced)} / ${fmtInt(closed.length)}`],
              ['อัตราเงินตรง', fmtPct(balancedPct)],
            ].map(([label, value]) => (
              <div key={label} className="min-w-0 bg-surface px-3 py-4 sm:px-4">
                <dt className="text-sm-label font-bold text-ink-3">{label}</dt>
                <dd className="num mt-1 break-words text-lg font-bold text-ink">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="border-t border-line bg-surface">
          <div className="flex flex-col gap-1 px-4 py-4 sm:px-6">
            <h3 className="text-h3 font-bold text-ink">คิวตรวจสอบรอบขาย</h3>
            <p className="text-sm-label text-ink-3">
              เรียงตามลำดับ ขาด → เกิน → ยังเปิด → เงินตรง
            </p>
          </div>
          {!ready || isLoading ? (
            <div className="h-40 animate-pulse bg-bg-warm" />
          ) : (
            <DataTable columns={columns} data={reviewRows} mobileCards />
          )}
        </div>
      </section>

      <section className="mb-6" aria-labelledby="round-context-heading">
        <div className="mb-3">
          <div className="text-sm-label font-bold uppercase tracking-[0.12em] text-primary">
            กราฟส่วนต่างเงินสด
          </div>
          <h2
            id="round-context-heading"
            className="mt-1 text-h3 font-bold text-ink"
          >
            ดูส่วนต่างแยกตามรอบและสาขา
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card title="ส่วนต่างต่อรอบ (เฉพาะรอบที่ปิด)">
            <Suspense
              fallback={
                <div
                  className="bo-card animate-pulse"
                  style={{ height: CHART_H.md }}
                />
              }
            >
              <BarCompare
                data={diffChart}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          </Card>
          <Card title="ส่วนต่างสะสมแยกสาขา">
            <Suspense
              fallback={
                <div
                  className="bo-card animate-pulse"
                  style={{ height: CHART_H.md }}
                />
              }
            >
              <BarCompare
                data={byBranch}
                colorByIndex
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
                showValues
              />
            </Suspense>
          </Card>
        </div>
      </section>
    </div>
  );
}
